import { useEffect, useRef, useState } from 'react'

import { IconBackhoe } from '@tabler/icons-react'
import Image from 'next/image'
import styled, { keyframes } from 'styled-components'

import logo from 'public/buildpass-small.svg'

const SIZE = '100px'
const MOVE = 2

const Wrapper = styled.div`
  position: relative;
  width: ${SIZE};
  height: ${SIZE};
  margin-left: auto;
  margin-right: auto;
`

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`

const Bg = styled.div`
  width: ${SIZE};
  height: ${SIZE};
  background: linear-gradient(303.77deg, #d1d4e5 -10.44%, #eeecfa 100%);
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`

const IconWrapper = styled.div`
  position: absolute;
  top: -18px;
  left: -32px;
  color: ${({ theme }) => theme.colour.secondary._600};
  transition: transform 0.2s;
  transition-timing-function: linear;
  z-index: 1;
`

const LogoWrapper = styled.div`
  position: absolute;
  top: 47px;
  left: 92px;
  width: 20px;
  height: 30px;
  overflow: hidden;
  transform: rotate(45deg);
`

const Text = styled.p`
  ${({ theme }) => theme.font.caption01};
  color: ${({ theme }) => theme.colour.secondary._300};
  margin-top: 0.25rem;
`

export const ErrorIcon = () => {
  const backHoe = useRef<HTMLDivElement>(undefined)

  const arrows = useRef({
    up: false,
    left: false,
    right: false,
    down: false,
  })

  const [coords, setCoords] = useState({ x: -32, y: -18 })
  const [direction, setDirection] = useState<'left' | 'right'>('right')

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') {
        arrows.current = { ...arrows.current, right: true }
      }
      if (event.key === 'ArrowLeft') {
        arrows.current = { ...arrows.current, left: true }
      }
      if (event.key === 'ArrowUp') {
        arrows.current = { ...arrows.current, up: true }
      }
      if (event.key === 'ArrowDown') {
        arrows.current = { ...arrows.current, down: true }
      }
    }

    const onKeyUp = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') {
        arrows.current = { ...arrows.current, right: false }
      }
      if (event.key === 'ArrowLeft') {
        arrows.current = { ...arrows.current, left: false }
      }
      if (event.key === 'ArrowUp') {
        arrows.current = { ...arrows.current, up: false }
      }
      if (event.key === 'ArrowDown') {
        arrows.current = { ...arrows.current, down: false }
      }
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('keyup', onKeyUp)

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('keyup', onKeyUp)
    }
  }, [])

  useEffect(() => {
    let animatiionNumber

    const loop = () => {
      setDirection((currentDir) => {
        if (arrows.current?.right) return 'right'
        if (arrows.current?.left) return 'left'
        return currentDir
      })

      setCoords((coords) => {
        let x = coords.x
        if (arrows.current?.left) {
          x -= MOVE
        } else if (arrows.current?.right) {
          x += MOVE
        }

        let y = coords.y
        if (arrows.current?.up) {
          y -= MOVE
        } else if (arrows.current?.down) {
          y += MOVE
        }

        return { x, y }
      })

      animatiionNumber = requestAnimationFrame(loop)
    }

    loop()

    return () => {
      cancelAnimationFrame(animatiionNumber)
    }
  }, [arrows])

  return (
    <>
      <Wrapper>
        <Bg />
        <IconWrapper
          ref={backHoe}
          style={{
            transform: `rotateY(${direction === 'left' ? '180deg' : '0'})`,
            top: `${coords.y}px`,
            left: `${coords.x}px`,
          }}
        >
          <IconBackhoe size={140} stroke={0.6} />
          <LogoWrapper>
            <Image
              src={logo.src || ''}
              width={30}
              height={30}
              alt="BuildPass logo"
            />
          </LogoWrapper>
        </IconWrapper>
      </Wrapper>
      <Text>play with the ←↑↓→ keys</Text>
    </>
  )
}
